import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { 
  Search, X, ChevronRight, Book, Code, FileText, 
  HelpCircle, Shield, Wallet, Bot, Terminal, GitBranch,
  Zap, Smartphone, Users, FileCheck
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { GettingStartedContent } from "@/components/documentation/GettingStartedContent";
import { AdvancedFeaturesContent } from "@/components/documentation/AdvancedFeaturesContent";
import { SecurityContent } from "@/components/documentation/SecurityContent";
import { MarketplaceContent } from "@/components/documentation/MarketplaceContent";
import { ApiReferenceContent } from "@/components/documentation/ApiReferenceContent";
import { UserGuideContent } from "@/components/documentation/UserGuideContent";
import { MobileAppContent } from "@/components/documentation/MobileAppContent";
import { TroubleshootingContent } from "@/components/documentation/TroubleshootingContent";
import { TeamBuilderContent } from "@/components/documentation/TeamBuilderContent";
import { AdminGuideContent } from "@/components/documentation/AdminGuideContent";
import { DevResourcesContent } from "@/components/documentation/DevResourcesContent";
import { ReleaseNotesContent } from "@/components/documentation/ReleaseNotesContent";
import { ComplianceContent } from "@/components/documentation/ComplianceContent";
import { AuthenticationMethodsContent } from "@/components/documentation/AuthenticationMethodsContent";

const Documentation = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState("getting-started");
  const [activePage, setActivePage] = useState("platform-overview");
  const [showSearch, setShowSearch] = useState(false);
  const [bookmarkedArticles, setBookmarkedArticles] = useState<string[]>([]);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<Record<string, boolean>>({});

  const location = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get("section");
    const page = params.get("page");

    if (section) setActiveSection(section);
    if (page) setActivePage(page);

    window.scrollTo(0, 0);
  }, [location]);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      toast({
        title: "Search results",
        description: `Found 5 results for "${searchQuery}"`,
      });
      setShowSearch(false);
    }
  };

  const toggleBookmark = (articleId: string) => {
    setBookmarkedArticles(prev => 
      prev.includes(articleId) 
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId]
    );
    
    toast({
      title: bookmarkedArticles.includes(articleId) ? "Bookmark removed" : "Bookmark added",
      description: bookmarkedArticles.includes(articleId) 
        ? "Article removed from your bookmarks" 
        : "Article added to your bookmarks",
      variant: "success",
    });
  };

  const submitFeedback = (articleId: string, isHelpful: boolean) => {
    setFeedbackSubmitted(prev => ({
      ...prev,
      [articleId]: true
    }));
    
    toast({
      title: "Thank you for your feedback!",
      description: isHelpful 
        ? "We're glad you found this helpful" 
        : "We'll work on improving this content",
      variant: "success",
    });
  };

  const documentationSections = [
    {
      id: "getting-started",
      label: "Getting Started",
      icon: <Book className="h-4 w-4" />,
      pages: [
        { id: "platform-overview", label: "Platform Overview" },
        { id: "quick-start-guide", label: "Quick Start Guide" },
        { id: "account-setup", label: "Account Setup" },
        { id: "dashboard-tour", label: "Dashboard Tour" },
        { id: "glossary", label: "Glossary of Terms" }
      ]
    },
    {
      id: "user-guide",
      label: "User Guide",
      icon: <FileText className="h-4 w-4" />,
      pages: [
        { id: "profile-management", label: "Profile Management" },
        { id: "project-creation", label: "Project Creation" },
        { id: "digital-portfolio", label: "Digital Portfolio" },
        { id: "collaboration", label: "Collaboration Tools" },
        { id: "messaging", label: "Messaging System" },
        { id: "proposal-workflow", label: "Proposal Workflow" },
        { id: "reviews-ratings", label: "Reviews & Ratings" }
      ]
    },
    {
      id: "advanced-features",
      label: "Advanced Features",
      icon: <Zap className="h-4 w-4" />,
      pages: [
        { id: "ai-job-matching", label: "AI Job Matching" },
        { id: "ai-agents", label: "AI Agents" },
        { id: "team-builder", label: "Team Builder" },
        { id: "enterprise-integration", label: "Enterprise Integration" },
        { id: "skills-test", label: "Skills Testing" },
        { id: "analytics", label: "Analytics & Reports" }
      ]
    },
    {
      id: "marketplace",
      label: "Marketplace",
      icon: <Wallet className="h-4 w-4" />,
      pages: [
        { id: "marketplace-overview", label: "Marketplace Overview" },
        { id: "buying-services", label: "Buying Services" },
        { id: "selling-services", label: "Selling Services" },
        { id: "payments", label: "Payments & Transactions" },
        { id: "escrow-service", label: "Escrow Service" }
      ]
    },
    {
      id: "api-reference",
      label: "API Reference",
      icon: <Code className="h-4 w-4" />,
      pages: [
        { id: "api-overview", label: "API Overview" },
        { id: "authentication", label: "Authentication" },
        { id: "endpoints", label: "Endpoints" },
        { id: "webhooks", label: "Webhooks" },
        { id: "rate-limits", label: "Rate Limits" },
        { id: "error-handling", label: "Error Handling" }
      ]
    },
    {
      id: "mobile-app",
      label: "Mobile App",
      icon: <Smartphone className="h-4 w-4" />,
      pages: [
        { id: "mobile-overview", label: "Mobile Overview" },
        { id: "ios-guide", label: "iOS Guide" },
        { id: "android-guide", label: "Android Guide" },
        { id: "notifications", label: "Notifications" },
        { id: "offline-access", label: "Offline Access" }
      ]
    },
    {
      id: "security",
      label: "Security & Privacy",
      icon: <Shield className="h-4 w-4" />,
      pages: [
        { id: "security-overview", label: "Security Overview" },
        { id: "data-privacy", label: "Data Privacy" },
        { id: "compliance", label: "Compliance" },
        { id: "authentication-methods", label: "Authentication Methods" },
        { id: "best-practices", label: "Security Best Practices" }
      ]
    },
    {
      id: "troubleshooting",
      label: "Troubleshooting",
      icon: <HelpCircle className="h-4 w-4" />,
      pages: [
        { id: "common-issues", label: "Common Issues" },
        { id: "error-messages", label: "Error Messages" },
        { id: "account-issues", label: "Account Issues" },
        { id: "payment-issues", label: "Payment Issues" },
        { id: "contact-support", label: "Contact Support" }
      ]
    },
    {
      id: "admin",
      label: "Admin Guide",
      icon: <Users className="h-4 w-4" />,
      pages: [
        { id: "admin-overview", label: "Admin Overview" },
        { id: "user-management", label: "User Management" },
        { id: "content-moderation", label: "Content Moderation" },
        { id: "system-settings", label: "System Settings" },
        { id: "analytics-dashboard", label: "Analytics Dashboard" }
      ]
    },
    {
      id: "developer-resources",
      label: "Developer Resources",
      icon: <Terminal className="h-4 w-4" />,
      pages: [
        { id: "developer-overview", label: "Developer Overview" },
        { id: "sdk-libraries", label: "SDK & Libraries" },
        { id: "integrations", label: "Integrations" },
        { id: "custom-widgets", label: "Custom Widgets" },
        { id: "extension-marketplace", label: "Extension Marketplace" }
      ]
    },
    {
      id: "release-notes",
      label: "Release Notes",
      icon: <GitBranch className="h-4 w-4" />,
      pages: [
        { id: "latest-release", label: "Latest Release" },
        { id: "previous-releases", label: "Previous Releases" },
        { id: "upcoming-features", label: "Upcoming Features" },
        { id: "deprecations", label: "Deprecations" },
        { id: "migration-guides", label: "Migration Guides" }
      ]
    }
  ];

  const currentSection = documentationSections.find(section => section.id === activeSection);
  const currentPage = currentSection?.pages.find(page => page.id === activePage);

  const breadcrumb = currentSection && currentPage ? (
    <div className="flex items-center text-sm text-gray-500 mb-4">
      <Link to="/documentation" className="hover:text-gray-700">Documentation</Link>
      <ChevronRight className="h-4 w-4 mx-1" />
      <Link to={`/documentation?section=${activeSection}`} className="hover:text-gray-700">
        {currentSection.label}
      </Link>
      <ChevronRight className="h-4 w-4 mx-1" />
      <span className="text-gray-900 font-medium">{currentPage.label}</span>
    </div>
  ) : null;

  const renderDocumentContent = () => {
    if (activeSection === "getting-started") {
      return (
        <GettingStartedContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "user-guide") {
      return (
        <UserGuideContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "advanced-features") {
      return (
        <AdvancedFeaturesContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "security") {
      return (
        <SecurityContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "security" && activePage === "compliance") {
      return (
        <ComplianceContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "security" && activePage === "authentication-methods") {
      return (
        <AuthenticationMethodsContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "marketplace") {
      return (
        <MarketplaceContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "api-reference") {
      return (
        <ApiReferenceContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "mobile-app") {
      return (
        <MobileAppContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "troubleshooting") {
      return (
        <TroubleshootingContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "advanced-features" && activePage === "team-builder") {
      return (
        <TeamBuilderContent 
          activePage={"team-builder"} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "admin") {
      return (
        <AdminGuideContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "developer-resources") {
      return (
        <DevResourcesContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    if (activeSection === "release-notes") {
      return (
        <ReleaseNotesContent 
          activePage={activePage} 
          feedbackSubmitted={feedbackSubmitted}
          bookmarkedArticles={bookmarkedArticles}
          submitFeedback={submitFeedback}
          toggleBookmark={toggleBookmark}
        />
      );
    }

    return (
      <div className="flex items-center justify-center h-[400px] bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <div className="text-center max-w-md p-6">
          <HelpCircle className="h-10 w-10 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Documentation Coming Soon</h3>
          <p className="text-gray-500">
            We're currently working on this documentation section. Check back soon for updates!
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Documentation</h1>
        <div className="relative">
          {showSearch ? (
            <div className="flex items-center bg-white rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-blue-600">
              <Input
                type="text"
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-0 ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <div className="flex items-center pr-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSearch(false)}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSearch(true)}
              className="flex items-center"
            >
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden sticky top-20">
            <ScrollArea className="h-[calc(100vh-160px)]">
              <div className="p-4 space-y-6">
                {documentationSections.map((section) => (
                  <div key={section.id} className="space-y-1">
                    <Link
                      to={`/documentation?section=${section.id}`}
                      className={`flex items-center px-2 py-1.5 text-sm font-medium rounded-md ${
                        activeSection === section.id
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        setActiveSection(section.id);
                        setActivePage(section.pages[0].id);
                      }}
                    >
                      <span className="mr-2">{section.icon}</span>
                      {section.label}
                    </Link>
                    {activeSection === section.id && (
                      <div className="ml-6 space-y-1 pt-1">
                        {section.pages.map((page) => (
                          <Link
                            key={page.id}
                            to={`/documentation?section=${section.id}&page=${page.id}`}
                            className={`block px-2 py-1.5 text-sm rounded-md ${
                              activePage === page.id
                                ? "bg-gray-100 text-gray-900 font-medium"
                                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                            onClick={() => setActivePage(page.id)}
                          >
                            {page.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {breadcrumb}
            <Separator className="mb-6" />
            
            {renderDocumentContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
